import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight, FaUser, FaFilter, FaSearch, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import AdvancedFilter from './AdvancedFilter';
import { apiService } from '../Config/api.service';
import { OPERATORS_BY_TYPE } from '../Constantes/OperatorByType';
import LoadingBarWithStyles from '../Utils/LoadingBarWithStyles';
import AvatarCircle from '../Utils/AvatarCircle';

const TableList = ({
  columns,
  data,
  path,
  searchDisplay = true,
  searchQuery,
  setSearchQuery,
  filterBy,
  setFilterBy,
  showFilter = true,
  onSort,
  sortBy,
  pagination,
  onPageChange,
  onRowClick,
  model,
  selectable = false,
  extraActions,
  onSelectedRowsChange,
  selectActions,
  toolbarActions,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const currentPage = pagination?.page || localCurrentPage;
  const totalPages = pagination?.total_pages || 1;
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [fields, setFields] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [searchTerms, setSearchTerms] = useState(
    Array.isArray(searchQuery) ? searchQuery : searchQuery ? [searchQuery] : []
  );
  const [allPagesSelected, setAllPagesSelected] = useState(false);

  // Memoize all row ids for select all
  const allRowIds = useMemo(() => data.map(row => row.id), [data]);
  const isAllSelected = data.length > 0 && selectedRows.length === data.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < data.length;

  useEffect(() => {
    if (onSelectedRowsChange) onSelectedRowsChange(selectedRows);
  }, [selectedRows, onSelectedRowsChange]);

  const handlePageChange = useCallback(
    (pageNumber) => {
      if (onPageChange) {
        onPageChange(pageNumber, searchTerms.length ? searchTerms : inputValue, filterBy);
      } else {
        setLocalCurrentPage(pageNumber);
      }
    },
    [onPageChange, searchTerms, inputValue, filterBy]
  );

  const handleCheckboxChange = useCallback(
    (id) => {
      setSelectedRows(prev =>
        prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
      );
    },
    []
  );

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedRows(allRowIds);
      setAllPagesSelected(false);
    } else {
      setSelectedRows([]);
      setAllPagesSelected(false);
    }
  }, [allRowIds]);

  const handleSelectAllPages = async () => {
    // Fetch all IDs from backend (implement an API endpoint for this)
    const res = await apiService.getElement(`/all-ids/${model}`);
    setSelectedRows(res.data.ids);
    setAllPagesSelected(true);
  };

  const handleSearchKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        if (!searchTerms.includes(inputValue.trim())) {
          const newTerms = [...searchTerms, inputValue.trim()];
          setSearchTerms(newTerms);
          setSearchQuery(newTerms);
          setInputValue('');
          if (onPageChange) onPageChange(1, newTerms, filterBy);
        }
      }
    },
    [inputValue, searchTerms, setSearchQuery, onPageChange, filterBy]
  );

  const handleRemoveSearchTerm = useCallback(
    (term) => {
      const newTerms = searchTerms.filter(t => t !== term);
      setSearchTerms(newTerms);
      setSearchQuery(newTerms);
      if (onPageChange) onPageChange(1, newTerms, filterBy);
    },
    [searchTerms, setSearchQuery, onPageChange, filterBy]
  );

  const handleShowAdvancedFilter = useCallback(async () => {
    if (!showAdvancedFilter && fields.length === 0 && model) {
      try {
        const res = await apiService.getElement(`/fields/${model}`);
        const data = res.data;
        setFields(
          data.fields.map(f => ({
            label: f.string,
            value: f.name,
            type: f.type,
            relation: f.relation,
            selection: f.selection,
            name: f.name,
          }))
        );
      } catch {
        setFields([]);
      }
    }
    setShowAdvancedFilter(prev => !prev);
  }, [showAdvancedFilter, fields.length, model]);

  // Badge rendering modernisé
  const renderBadge = (label, onRemove) => (
    <span className="inline-flex items-center gap-2 px-3 py-1 text-sm bg-black-100 text-black-800 rounded-full border border-black-200">
      {label}
      <button
        type="button"
        className="flex items-center justify-center w-4 h-4 text-black-600 hover:text-red-500 hover:bg-red-100 rounded-full transition-colors duration-200"
        onClick={onRemove}
        aria-label={t('hspForm.delete')}
      >
        <FaTimes size={10} />
      </button>
    </span>
  );

  return (
    <div className="space-y-4">
      <div className='bg-white p-2 rounded-lg'>
      {/* Filtres appliqués */}
      {filterBy?.filters?.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {filterBy.filters.map((f, idx) => {
            const field = fields?.find(fl => fl.value === f.field);
            const label = field?.label || field?.name || f.field;
            const op = (field && OPERATORS_BY_TYPE[field.type]?.find(o => o.value === f.operator)?.label) || f.operator;
            let valueLabel = f.value;
            if (field?.type === 'selection' && Array.isArray(field.selection)) {
              const sel = field.selection.find(([val]) => val === f.value);
              valueLabel = sel ? sel[1] : f.value;
            }
            return renderBadge(
              `${label} ${op} ${valueLabel}`,
              () => {
                const newFilters = filterBy.filters.filter((_, i) => i !== idx);
                setFilterBy({ ...filterBy, filters: newFilters });
                if (onPageChange) onPageChange(1, searchTerms, { ...filterBy, filters: newFilters });
              }
            );
          })}
        </div>
      )}

      {/* Termes de recherche */}
      {searchTerms.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {searchTerms.map((term, idx) =>
            renderBadge(term, () => handleRemoveSearchTerm(term))
          )}
        </div>
      )}

      {selectable && isAllSelected && !allPagesSelected && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded mb-2 flex items-center gap-2">
              {t('tableList.allPageItemsSelected')}
              <button
                className="underline text-black-600 ml-2"
                onClick={handleSelectAllPages}
                type="button"
              >
                {t('tableList.selectAllItems', { count: pagination?.total_items || '...' })}
              </button>
              <button
                className="ml-auto text-gray-500 hover:text-red-500"
                onClick={() => { setSelectedRows([]); setAllPagesSelected(false); }}
                type="button"
              >
                {t('tableList.deselectAll')}
              </button>
            </div>
          )}
          {selectable && allPagesSelected && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded mb-2 flex items-center gap-2">
              {t('tableList.allItemsSelected', { count: selectedRows.length })}
              <button
                className="ml-auto text-gray-500 hover:text-red-500"
                onClick={() => { setSelectedRows([]); setAllPagesSelected(false); }}
                type="button"
              >
                {t('tableList.deselectAll')}
              </button>
            </div>
          )}

      {/* Barre de recherche et filtres */}
      {searchDisplay && (
        <div className="flex gap-4 items-center flex-wrap ">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              id="tablelist-search"
              name="tablelist-search"
              placeholder={t('tableList.searchPlaceholder')}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyUp={handleSearchKeyPress}
              className="w-full pl-10 pr-4 py-2 border border-[--color-primary] rounded-lg focus:ring-2 focus:ring-black-500 focus:border-black-500 transition-colors duration-200"
            />
          </div>
          
          {showFilter && (
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 text-black-600 border border-black-300 rounded-lg hover:bg-black-50 transition-colors duration-200"
                onClick={handleShowAdvancedFilter}
              >
                <FaFilter size={14} />
                {t('tableList.filters')}
              </button>
              {showAdvancedFilter && (
                <div className="absolute right-0 z-50 mt-2 w-[550px]">
                  <AdvancedFilter
                    fields={fields}
                    onApply={(filters, includeArchived, globalLogic) => {
                      setShowAdvancedFilter(false);
                      setFilterBy({ filters, includeArchived, globalLogic });
                      if (onPageChange) onPageChange(1, searchTerms, { filters, includeArchived, globalLogic });
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {toolbarActions && (
            <div className="flex items-center gap-3 flex-wrap">
              {typeof toolbarActions === 'function' ? toolbarActions() : toolbarActions}
            </div>
          )}

          {/* Actions de sélection */}
          {selectedRows.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                {t('tableList.selectedCount', { count: selectedRows.length })}
              </span>
              {typeof selectActions === 'function'
                ? selectActions(selectedRows)
                : selectActions
              }
            </div>
          )}
        </div>
      )}

      </div>

      {/* Table */}
      <div className="bg-white rounded-lg overflow-hidden">
        {loading && <LoadingBarWithStyles />}
        
        {!data?.length > 0 && loading ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {selectable && (
                    <th className="px-6 py-3 w-12">
                      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </th>
                  )}
                  {columns.map((_, index) => (
                    <th key={index} className="px-6 py-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                    </th>
                  ))}
                  {extraActions && (
                    <th className="px-6 py-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[...Array(8)].map((_, idx) => (
                  <tr key={idx}>
                    {selectable && (
                      <td className="px-6 py-4">
                        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                      </td>
                    )}
                    {columns.map((_, colIdx) => (
                      <td key={colIdx} className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                      </td>
                    ))}
                    {extraActions && (
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {selectable && (
                    <th className="px-6 py-3 w-12">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-black-600 border-gray-300 rounded focus:ring-black-500"
                        checked={isAllSelected}
                        ref={el => {
                          if (el) el.indeterminate = isIndeterminate;
                        }}
                        onChange={e => handleSelectAll(e.target.checked)}
                      />
                    </th>
                  )}
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        column.sortable ? 'cursor-pointer select-none hover:bg-gray-100 transition-colors duration-200' : ''
                      }`}
                      onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                    >
                      <div className="flex items-center gap-2">
                        {column.label || column}
                        {column.sortable && sortBy?.key === column.key && (
                          <span className="text-black-500">
                            {sortBy.direction === 'asc' ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  {extraActions && <th className="px-6 py-3 w-20"></th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + (selectable ? 1 : 0) + (extraActions ? 1 : 0)} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="bg-gray-100 rounded-full p-3 mb-3">
                          <FaSearch className="text-gray-400" size={24} />
                        </div>
                        <p className="text-lg font-medium text-gray-900 mb-1">{t('tableList.noResultFound')}</p>
                        <p className="text-sm text-gray-500">{t('tableList.tryModifyingCriteria')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`hover:bg-gray-50 transition-colors duration-200 ${onRowClick ? 'cursor-pointer' : ''}`}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {selectable && (
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-black-600 border-gray-300 rounded focus:ring-black-500"
                            checked={selectedRows.includes(row.id)}
                            onChange={() => handleCheckboxChange(row.id)}
                          />
                        </td>
                      )}
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {column.render
                            ? column.render(row)
                            : column.key === 'name' && column.avatar === true ? (
                              <div>
                                <Link className="font-semibold flex items-center gap-3 text-gray-900 hover:text-black-600 transition-colors duration-200" to={`/${path}/${row.id}${window.location.search}`}>
                                  <div className="relative flex-shrink-0">
                                    {row['image'] ? (
                                      <img src={`data:image/png;base64,${row['image']}`} alt="" className="w-8 h-8 object-cover rounded-full" />
                                    ) : (
                                      <AvatarCircle
                                        name={row[column.key || column]}
                                        size={32}
                                      />
                                    )}
                                    {row['contract_expiration_pending'] && (
                                      <span
                                        className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-500 border-2 border-white rounded-full"
                                        title={t('contractExpiration.expiredTitle')}
                                      />
                                    )}
                                  </div>
                                  {row[column.key || column]}
                                </Link>
                              </div>
                            ) : column.key === 'Image' || column === 'Image' ? (
                              row[column.key || column] ? (
                                <img src={row[column.key || column]} alt="" className="w-8 h-8 object-cover rounded-full" />
                              ) : (
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                  <FaUser className="w-4 h-4 text-gray-400" />
                                </div>
                              )
                            ) : column.key === 'Statut' || column === 'Statut' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {row[column.key || column]}
                              </span>
                            ) : path ? (
                              <Link 
                                to={`/${path}/${row.id}${window.location.search}`}
                                className="text-black-600 hover:text-black-800 transition-colors duration-200"
                              >
                                {row[column.key || column]}
                              </Link>
                            ) : (
                              row[column.key || column]
                            )}
                        </td>
                      ))}
                      {extraActions && (
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          {typeof extraActions === 'function'
                            ? extraActions(row)
                            : extraActions}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {t('employeePayslipNav.page', { current: currentPage, total: totalPages })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <FaArrowLeft size={12} />
                  {t('dailySalaryInputs.previous')}
                </button>
                
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = index + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + index;
                    } else {
                      pageNumber = currentPage - 2 + index;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                          currentPage === pageNumber
                            ? 'bg-black-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t('dailySalaryInputs.next')}
                  <FaArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TableList.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  path: PropTypes.string,
  searchDisplay: PropTypes.bool,
  searchQuery: PropTypes.any,
  setSearchQuery: PropTypes.func,
  filterBy: PropTypes.any,
  setFilterBy: PropTypes.func,
  pagination: PropTypes.object,
  onPageChange: PropTypes.func,
  onRowClick: PropTypes.func,
  model: PropTypes.string,
  selectable: PropTypes.bool,
  extraActions: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  toolbarActions: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
};

export default TableList;
